/**
 * AuditLog HTTP client.
 */
import api from "./api";
import type {
  AuditLogEntry,
  AuditLogSearchFilters,
  AuditLogSearchResponse,
} from "@/types/auditLog.types";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

function unwrap<T>(res: { data: ApiEnvelope<T> | T }): T {
  const body = res.data as ApiEnvelope<T> | T;
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as Record<string, unknown>) &&
    "success" in (body as Record<string, unknown>)
  ) {
    return (body as ApiEnvelope<T>).data;
  }
  return body as T;
}

function toParams(filters: AuditLogSearchFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

export const auditLogService = {
  async search(
    filters: AuditLogSearchFilters = {},
  ): Promise<AuditLogSearchResponse> {
    const res = await api.get("/audit-log", { params: toParams(filters) });
    return unwrap<AuditLogSearchResponse>(res);
  },

  async getById(id: string): Promise<AuditLogEntry> {
    const res = await api.get(`/audit-log/${id}`);
    return unwrap<AuditLogEntry>(res);
  },

  /**
   * Triggers a browser download of the CSV export.
   * Returns the Blob URL in case the caller wants to revoke it.
   */
  async downloadCsv(filters: AuditLogSearchFilters = {}): Promise<void> {
    const res = await api.get("/audit-log/export.csv", {
      params: toParams(filters),
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `audit-log-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  },
};
